import { createContext, useContext, useSyncExternalStore, type ReactElement, type ReactNode } from "react";
import type { Rules } from "odyssey-shared";
import type { ArtBox, IconArt } from "../../ui/kit/ArtIcon.tsx";
import { iconArt, type FallbackLog, type IconsSnapshot } from "./art.ts";
import { NO_ICONS, type IconSource } from "./page-icons.ts";

/**
 * How the HUD's places read the page's icons: one source for a React tree, given at its root (`IconsProvider`), read by
 * a place once per render (`useIcons`) and asked per thing (`itemArt`). A tree without a provider has no icons and
 * draws every glyph.
 */

const IconsContext = createContext<IconSource>(NO_ICONS);

/**
 * What a place holds for one render: the snapshot and the log.
 */
export interface IconsView {
  readonly snapshot: IconsSnapshot;
  readonly log: FallbackLog;
}

/**
 * Gives a tree its icons.
 *
 * @param props The `source`, and the tree.
 * @returns The provider.
 */
export function IconsProvider({ source, children }: { readonly source: IconSource; readonly children: ReactNode }): ReactElement {
  return <IconsContext.Provider value={source}>{children}</IconsContext.Provider>;
}

/**
 * The tree's icons for this render; the place renders again when the atlas arrives or the UI scale changes.
 *
 * @returns The snapshot and the log.
 */
export function useIcons(): IconsView {
  const source = useContext(IconsContext);
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  return { snapshot, log: source.log };
}

/**
 * The art an item is drawn with in a box, or null for its glyph.
 *
 * @param icons The tree's icons for this render.
 * @param rules The rules the item is read from.
 * @param item The item's rules id.
 * @param box The box the place scales art for.
 * @returns The picture, or null.
 * @example
 * itemArt(icons, PAGE_RULES, "minor_healing_potion", 22);
 * // the red flask once the atlas has decoded at UI scale 2 or more
 */
export function itemArt(icons: IconsView, rules: Rules, item: string, box: ArtBox): IconArt | null {
  return iconArt(icons.snapshot, rules.items.get(item)?.icon ?? null, `item ${item}`, box, icons.log);
}
